/* StaffModal.js */
import React, { useMemo, useState } from 'react';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomTypography from 'components/Fileds/CustomTypography';
import * as Yup from 'yup';
import GenericDialog from '../GenericDialog/GenericDialog';
import { Box, IconButton, useTheme } from '@mui/material';
import CustomSelect from 'components/Fileds/CustomSelect';
import { manageAccountRoles } from 'utils/constant';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CustomPhoneContryCodeInput from 'components/Fileds/CustomPhoneContryCodeInput';
import { useSelector } from 'react-redux';
import { getOrderTypePermissions, isPermissionAllowed } from 'utils/function';

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const getStaffSchema = (isEdit) =>
    Yup.object().shape({
    full_name: Yup.string().required('Name is required'),
    email: Yup.string().required('Email is required').email('Invalid Email'),
    phone: Yup.string().required('Phone is required'),
    password: isEdit
            ? Yup.string().nullable().notRequired().test('password-complexity', 'Password must include uppercase, lowercase, number & special character (min 8 chars)', (value) => {
                if (!value) return true;
                return passwordRegex.test(value);
            })
            : Yup.string().required('Password is required').matches(passwordRegex, 'Password must include uppercase, lowercase, number & special character (min 8 chars)'),
        role: Yup.string().required('Role is required'),
});

const StaffModal = ({ open, onClose, initialData, onSubmit, loading }) => {
    const posData = useSelector((state) => state?.posData);
    const vendorCountry = posData?.country_code;
    const isEdit = Boolean(initialData);
    const permissions = useMemo(() => getOrderTypePermissions(posData), [posData]);
    const isEmenuAllowed = isPermissionAllowed(permissions, 'permission_pos_e_menu');

    const roleOptions = useMemo(() => {
        return manageAccountRoles.filter((role) => {
            if (role.value === 'emenu' && !isEmenuAllowed) {
                return isEdit && initialData?.role === 'emenu';
            }
            return true;
        });
    }, [isEmenuAllowed, isEdit, initialData?.role]);

    const title = isEdit ? 'Edit Staff Account' : 'Add Staff Account';
    const theme = useTheme();
    const [showPassword, setShowPassword] = useState(false);
    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    return (
        <GenericDialog
            open={open}
            onClose={onClose}
            title={title}
            initialValues={{
                full_name: initialData?.full_name || '',
                email: initialData?.email || '',
                password: initialData?.password || '',
                phone: initialData?.phone || '',
                country_code: initialData?.country_code || vendorCountry,
                role: initialData?.role || '',
            }}
            validationSchema={getStaffSchema(isEdit)}
            onSubmit={onSubmit}
            submitLabel={isEdit ? 'Update' : 'Save'}
            loading={loading}
            requiredFields={['full_name', 'email', 'phone', 'role'].concat(isEdit ? [] : ['password'])}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                <>
                    <Box sx={{ mb: '20px' }}>
                        <CustomTypography
                            label="Full Name"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
                        />
                        <CustomTextField
                            name="full_name"
                            value={values.full_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            placeholder="Enter Full Name"
                            error={touched.full_name && Boolean(errors.full_name)}
                            helperText={touched.full_name && errors.full_name}
                        />
                    </Box>
                    <Box sx={{ mb: '20px' }}>
                        <CustomTypography
                            label="Email"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
                        />
                        <CustomTextField
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            placeholder="Enter Email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                        />
                    </Box>
                    <Box sx={{ mb: '20px' }}>
                        <CustomTypography
                            label="Mobile Number"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
                        />
                        {/* <CustomTextField
                            name="phone"
                            value={values.phone}
                            onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                                setFieldValue('phone', digits);
                            }}
                            onBlur={handleBlur}
                            fullWidth
                            placeholder="Enter Mobile Number"
                            error={touched.phone && Boolean(errors.phone)}
                            helperText={touched.phone && errors.phone}
                        /> */}
                        <CustomPhoneContryCodeInput
                            value={values?.phone}
                            name="phone"
                            onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 14);
                                setFieldValue('phone', digits);
                            }}
                            onBlur={handleBlur}
                            placeholder="Enter Mobile Number"
                            error={touched.phone && Boolean(errors.phone)}
                            helperText={touched.phone && errors.phone}
                        />
                    </Box>
                    <Box sx={{ mb: (touched.password && Boolean(errors.password)) ? '40px' : '20px' }}>
                        <CustomTypography
                            label="Password"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
                        />
                        <CustomTextField
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            placeholder="Enter Password"
                            type={showPassword ? "text" : "password"}
                            endIcon={
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleTogglePasswordVisibility}
                                    edge="end"
                                >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            }
                        />
                    </Box>
                    <Box>
                        <CustomTypography
                            label="Roles"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
                        />
                        <CustomSelect
                            name="role"
                            value={values.role}
                            onChange={(e) => {
                                handleChange(e);
                                // Don't validate immediately
                                setFieldValue("role", e.target.value);
                            }}
                            onBlur={handleBlur}
                            error={touched.role && errors.role}
                            helperText={touched.role && errors.role}
                            placeholder={
                                "Select Roles"
                            }
                            options={roleOptions}
                        />
                    </Box>
                </>
            )}
        </GenericDialog>
    );
};

export default StaffModal;
